import React, { useCallback, useEffect, useState } from 'react';
import './modal-content.scss';
import HomeSVG from '@/assets/svg-kb/home.svg?react';
import { observer } from 'mobx-react-lite';
import { type KbStore } from './kb-store';
import { FileTreeNodeType, KbNode, UserAccess } from '@/services/kbService';
import { Spin, Tree, TreeDataNode } from 'antd';
import { extIconsMap, getExt } from '@/common/file-utils';
import { UserInfo } from '@/common/login-info';
import { CloseOutlined } from '@ant-design/icons';

const createTreeDataNode = (dataNode: KbNode): TreeDataNode => {
  let icon: React.ReactNode;
  if (dataNode.type === 2) {
    const ext = getExt(dataNode.title);
    icon = extIconsMap[ext] ? <img src={extIconsMap[ext]} /> : <img src={extIconsMap.unknown} />;
  } else {
    icon = <img src={extIconsMap.folder} />;
  }
  const access = typeof dataNode.access === 'undefined' ? UserAccess.None : dataNode.access;
  const invisible = dataNode.visible === 0 && dataNode.user_visible === 0
  return {
    title: dataNode.title,
    key: dataNode.uuid,
    icon,
    disabled: (access <= UserAccess.Readable && dataNode.type !== FileTreeNodeType.RootGroup) || !dataNode.file_id || invisible,
    children: [],
  };
};

const pushStack = (stack: any[], array: any[]) => {
  for (let i = array.length - 1; i >= 0; i--) {
    stack.push(array[i]);
  }
};

const buildTreeDataFromKbData = (data: KbNode[]): TreeDataNode[] => {
  const tree: TreeDataNode[] = [];
  data.forEach(dataNode => {
    tree.push(createTreeDataNode(dataNode));
  });
  const stack: KbNode[] = [];
  const treeStack: TreeDataNode[] = [];

  pushStack(stack, data);
  pushStack(treeStack, tree);

  while (stack.length) {
    const dataNode = stack.pop();
    const treeNode = treeStack.pop();
    if (dataNode.children) {
      pushStack(stack, dataNode.children);
      dataNode.children.forEach(ch => {
        const chTreeNode = createTreeDataNode(ch);
        treeNode.children?.push(chTreeNode);
      });
      pushStack(treeStack, treeNode.children);
    }
  }
  return tree;
};

const findTreeNodeChain = (
  id: string,
  chain: TreeDataNode[],
  treeData?: TreeDataNode[]
): TreeDataNode[] => {
  if (!treeData) {
    return [];
  }
  for (let i = 0; i < treeData.length; i++) {
    const node = treeData[i];
    if (node.key === id) {
      chain.push(node);
      return chain;
    } else {
      let newChain = findTreeNodeChain(id, [...chain, node], node.children);
      if (newChain[newChain.length - 1]?.key === id) {
        return newChain;
      }
    }
  }
  return [];
};

const KbTreeModalContent = observer(
  (props: {
    store: KbStore;
    title?: string;
    userInfo: UserInfo;
    showClose?: boolean;
    cancel?: () => void;
    confirm?: () => void;
  }) => {
    const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
    const [treeData, setTreeData] = useState<TreeDataNode[]>([]);
    const [expandedKeys, setExpandedKeys] = useState([]);

    const clear = () => {
      setCheckedKeys([]);
    };

    const onCheck = (checked: { checked: string[]; halfChecked: string[] } | string[]) => {
      if (Array.isArray(checked)) {
        setCheckedKeys(checked as string[]);
      } else {
        setCheckedKeys(checked.checked);
      }
    };

    useEffect(() => {
      const treeData = buildTreeDataFromKbData(props.store.kbTreeData);
      setTreeData(treeData);
      setExpandedKeys(props.store.expandedKbIds);
      setCheckedKeys(props.store.selectedKbIds);
    }, [props.store.kbTreeData, props.store.expandedKbIds, props.store.selectedKbIds]);

    const onConfirm = useCallback(() => {
      props.store.updateSelectedKbIds(checkedKeys);
      props.confirm?.();
    }, [props.store, checkedKeys]);

    const onExpand = useCallback((keys: string[]) => {
      setExpandedKeys(keys)
      props.store.updateExpandedKbIds(keys)
    }, [ props.store ])

    return (
      <Spin spinning={props.store.loading}>
        <div className="file-tree-modal-content-container">
          <div className="file-tree-title">
            {props.title}
            <span className="clear" onClick={clear}>
              清空已选
              {props.showClose && (
                <CloseOutlined
                  style={{ marginLeft: 18, color: '#757575', cursor: 'pointer' }}
                  onClick={props.cancel}
                />
              )}
            </span>
          </div>
          <div className="file-tree-warp">
            <div className="company-name">
              <HomeSVG />
              {props.userInfo.company_name}
            </div>
            <div className="scroller">
              <div style={{ padding: '0 16px'}}>
                <Tree
                  showIcon
                  treeData={treeData}
                  checkStrictly
                  checkable
                  expandedKeys={expandedKeys}
                  checkedKeys={checkedKeys}
                  onExpand={onExpand}
                  onCheck={onCheck}
                />
              </div>
            </div>
          </div>
          <div className="confirm-btn" onClick={onConfirm}>
            确定
          </div>
        </div>
      </Spin>
    );
  }
);

export default KbTreeModalContent;
